import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Edit, Plus, X } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { CurrencyIcon } from '../../utils/currencyIcon';

const BookingFeeCard = ({ partnerId, onFeeUpdate }) => {
  const [therapyFees, setTherapyFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasSubmittedFees, setHasSubmittedFees] = useState(false);
  const [testPaymentsEnabled, setTestPaymentsEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('individual');
  const [customTherapies, setCustomTherapies] = useState([]);
  const [newCustomType, setNewCustomType] = useState('');

  // Currency options
  const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  ];

  // Predefined therapy types
  const predefinedTypes = [
    { key: 'individual', label: 'Individual Therapy' },
    { key: 'couple', label: 'Couple Therapy' },
    { key: 'family', label: 'Family Therapy' },
  ];

  useEffect(() => {
    loadFeeSettings();
  }, [partnerId]);

  /**
   * Load existing fee settings
   */
  const loadFeeSettings = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch both fee settings and partner details to get test_payments_enabled
      const [feeResponse, partnerResponse] = await Promise.all([
        partnerAPI.getFeeSettings(partnerId),
        partnerAPI.getById(partnerId)
      ]);

      const therapyFeesData = feeResponse.data.feeSettings.therapy_fees || [];
      setTherapyFees(therapyFeesData);

      // Set test payments enabled status
      setTestPaymentsEnabled(partnerResponse.data.partner?.test_payments_enabled || false);

      // If fees have been submitted, set view mode (read-only)
      const hasFees = therapyFeesData.length > 0;
      setHasSubmittedFees(hasFees);
      setIsEditMode(!hasFees); // If no fees exist, start in edit mode

      // Extract custom therapies
      const custom = therapyFeesData.filter(tf => !tf.is_predefined);
      setCustomTherapies(custom);

      // If we have fees and are on "others" tab, switch to a tab with content
      if (hasFees && activeTab === 'others') {
        // Check if individual therapy exists, otherwise use first available
        const hasIndividual = therapyFeesData.find(tf => tf.therapy_type === 'individual');
        if (hasIndividual) {
          setActiveTab('individual');
        } else if (therapyFeesData.length > 0) {
          setActiveTab(therapyFeesData[0].therapy_type);
        }
      }
    } catch (error) {
      console.error('Failed to load fee settings:', error);
      setError('Failed to load fee settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get fee for a specific therapy type
   */
  const getFeeForType = (therapyType) => {
    const fee = therapyFees.find(tf => tf.therapy_type === therapyType);
    return fee ? { fee: fee.fee, currency: fee.currency, duration: fee.duration || '' } : { fee: '', currency: 'INR', duration: '' };
  };

  /**
   * Update fee for a specific therapy type
   */
  const updateFeeForType = (therapyType, fee, currency, duration, isPredefined = true) => {
    setTherapyFees(prev => {
      const existing = prev.find(tf => tf.therapy_type === therapyType);
      if (existing) {
        return prev.map(tf =>
          tf.therapy_type === therapyType
            ? { ...tf, fee: parseFloat(fee) || 0, currency: currency || 'INR', duration: parseInt(duration) || 0 }
            : tf
        );
      } else {
        return [
          ...prev,
          {
            therapy_type: therapyType,
            fee: parseFloat(fee) || 0,
            currency: currency || 'INR',
            duration: parseInt(duration) || 0,
            is_predefined: isPredefined
          }
        ];
      }
    });
    setError('');
    setSuccessMessage('');
  };

  /**
   * Remove fee for a specific therapy type
   */
  const removeFeeForType = (therapyType) => {
    setTherapyFees(prev => prev.filter(tf => tf.therapy_type !== therapyType));
    setCustomTherapies(prev => prev.filter(ct => ct.therapy_type !== therapyType));
  };

  /**
   * Add custom therapy type
   */
  const addCustomTherapy = () => {
    if (!newCustomType.trim()) {
      setError('Please enter a therapy type name');
      return;
    }

    // Normalize the therapy type name for comparison (lowercase)
    const normalizedNewType = newCustomType.trim().toLowerCase();

    // Check if already exists (case-insensitive)
    if (therapyFees.find(tf => tf.therapy_type.toLowerCase() === normalizedNewType)) {
      setError('This therapy type already exists');
      return;
    }

    // Check if it conflicts with predefined types
    const predefinedNames = ['individual', 'couple', 'family'];
    if (predefinedNames.includes(normalizedNewType)) {
      setError('This name is reserved for predefined therapy types');
      return;
    }

    const customTherapy = {
      therapy_type: newCustomType.trim(),
      fee: 0,
      currency: 'INR',
      duration: 0,
      is_predefined: false
    };

    setCustomTherapies(prev => [...prev, customTherapy]);
    setTherapyFees(prev => [...prev, customTherapy]);
    setNewCustomType('');
    setError('');
    setSuccessMessage(`"${newCustomType.trim()}" therapy type added successfully!`);

    // Switch to the newly created custom therapy tab
    setActiveTab(customTherapy.therapy_type);

    // Clear success message after 2 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 2000);
  };

  /**
   * Validate and submit fee settings
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Filter out fees with 0 or empty values
    const validFees = therapyFees.filter(tf => tf.fee && parseFloat(tf.fee) > 0);

    // Validate that at least one therapy fee is provided
    if (validFees.length === 0) {
      setError('Please set fee for at least one therapy type');
      return;
    }

    // Currency-specific minimum validation for INR and duration validation
    const minimumFee = testPaymentsEnabled ? 1 : 300;
    for (const therapyFee of validFees) {
      const fee = parseFloat(therapyFee.fee);
      if (isNaN(fee) || fee < 0) {
        setError(`Fee for ${therapyFee.therapy_type} must be a valid non-negative number`);
        return;
      }

      if (therapyFee.currency === 'INR' && fee < minimumFee) {
        setError(`Minimum fee for INR is Rs. ${minimumFee} (${therapyFee.therapy_type})`);
        return;
      }

      // Validate duration
      const duration = parseInt(therapyFee.duration);
      if (!therapyFee.duration || isNaN(duration) || duration <= 0) {
        setError(`Please enter a valid session duration in minutes for ${therapyFee.therapy_type}`);
        return;
      }
    }

    try {
      setSaving(true);

      const payload = {
        therapy_fees: validFees.map(tf => ({
          therapy_type: tf.therapy_type,
          fee: parseFloat(tf.fee),
          currency: tf.currency || 'INR',
          duration: parseInt(tf.duration),
          is_predefined: tf.is_predefined !== undefined ? tf.is_predefined : true
        }))
      };

      await partnerAPI.updateFeeSettings(partnerId, payload);

      setSuccessMessage('Fee settings saved successfully!');
      setHasSubmittedFees(true);
      setIsEditMode(false); // Switch back to view mode after successful submission

      // Notify parent component to refresh fee settings
      if (onFeeUpdate) {
        onFeeUpdate();
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Failed to save fee settings:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save fee settings. Please try again.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle edit button click
   */
  const handleEditClick = () => {
    setIsEditMode(true);
    setError('');
    setSuccessMessage('');

    // If currently on "Add New" tab, switch to a tab where fees can be edited
    if (activeTab === 'others') {
      setActiveTab('individual');
    }
  };

  /**
   * Render fee input for a therapy type
   */
  const renderFeeInput = (therapyType, label, isPredefined = true) => {
    const { fee, currency, duration } = getFeeForType(therapyType);
    const selectedCurrency = currencies.find(c => c.code === currency);

    return (
      <div>
        {!isEditMode && hasSubmittedFees && fee ? (
          /* View Mode */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Currency
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md bg-gray-50 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary">
                {selectedCurrency?.symbol} - {selectedCurrency?.name} ({selectedCurrency?.code})
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Fee per session
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md bg-gray-50 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary">
                {selectedCurrency?.symbol}{fee ? parseFloat(fee).toFixed(2) : '0.00'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Session Duration
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md bg-gray-50 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary">
                {duration ? `${duration} minutes` : 'Not set'}
              </div>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                value={currency}
                onChange={(e) => updateFeeForType(therapyType, fee, e.target.value, duration, isPredefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
              >
                {currencies.map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.symbol} - {curr.name} ({curr.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Fee per session <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-text-tertiary">
                  {selectedCurrency?.symbol}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={fee}
                  onChange={(e) => updateFeeForType(therapyType, e.target.value, currency, duration, isPredefined)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                />
              </div>
              {currency === 'INR' && (
                <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
                  Minimum: Rs. {testPaymentsEnabled ? '1 (Test Mode Enabled)' : '300'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Session Duration (minutes) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={duration}
                onChange={(e) => updateFeeForType(therapyType, fee, currency, e.target.value, isPredefined)}
                placeholder="e.g., 45, 60, 90"
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
                Enter the duration of each session in minutes
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-dark-text-primary">
        <CurrencyIcon className="h-5 w-5 mr-2 text-primary-600 dark:text-dark-primary-500" />
        Set Session Fee
      </h3>

      {!isEditMode && hasSubmittedFees && (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-dark-bg-tertiary border border-blue-200 dark:border-blue-800 rounded-md">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            To add more session types with different fees, click Edit.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 text-gray-400 dark:text-dark-text-tertiary animate-spin" />
          <span className="ml-2 text-gray-600 dark:text-dark-text-secondary">Loading fee settings...</span>
        </div>
      ) : (
        <>
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-md">
              <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-md">
              <p className="text-sm text-success-700 dark:text-success-400">{successMessage}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-4 border-b border-gray-200 dark:border-dark-border">
            <div className="flex flex-wrap -mb-px">
              {/* Predefined therapy type tabs - only show if they exist in therapyFees or in edit mode */}
              {predefinedTypes.map(type => {
                const hasFee = therapyFees.find(tf => tf.therapy_type === type.key);
                // Show individual therapy always, show others only if they have fees or we're in edit mode without submitted fees
                const shouldShow = type.key === 'individual' || hasFee || (isEditMode && !hasSubmittedFees);

                if (!shouldShow) return null;

                return (
                  <button
                    key={type.key}
                    onClick={() => setActiveTab(type.key)}
                    className={`group px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      activeTab === type.key
                        ? 'border-primary-600 text-primary-600 dark:border-dark-primary-500 dark:text-dark-primary-500'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary'
                    }`}
                  >
                    <span>{type.label}</span>
                    {/* Only show delete button for couple and family therapy types, not individual */}
                    {isEditMode && type.key !== 'individual' && (
                      <X
                        className="h-3 w-3 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Remove "${type.label}" therapy type?`)) {
                            removeFeeForType(type.key);
                            // Switch to 'individual' tab after deletion
                            setActiveTab('individual');
                          }
                        }}
                      />
                    )}
                  </button>
                );
              })}

              {/* Custom therapy type tabs */}
              {customTherapies.map(ct => (
                <button
                  key={ct.therapy_type}
                  onClick={() => setActiveTab(ct.therapy_type)}
                  className={`group px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === ct.therapy_type
                      ? 'border-primary-600 text-primary-600 dark:border-dark-primary-500 dark:text-dark-primary-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary'
                  }`}
                >
                  <span>{ct.therapy_type}</span>
                  {isEditMode && (
                    <X
                      className="h-3 w-3 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Remove "${ct.therapy_type}" therapy type?`)) {
                          removeFeeForType(ct.therapy_type);
                          // Switch to 'individual' tab after deletion
                          setActiveTab('individual');
                        }
                      }}
                    />
                  )}
                </button>
              ))}

              {/* Others tab - only show in edit mode for adding new types */}
              {isEditMode && (
                <button
                  onClick={() => setActiveTab('others')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                    activeTab === 'others'
                      ? 'border-primary-600 text-primary-600 dark:border-dark-primary-500 dark:text-dark-primary-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary'
                  }`}
                >
                  <Plus className="h-3 w-3" />
                  <span>Add New</span>
                </button>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Tab Content */}
            <div className="mb-6">
              {/* Predefined therapy type content */}
              {predefinedTypes.map(type => (
                activeTab === type.key && (
                  <div key={type.key}>
                    {renderFeeInput(type.key, type.label, true)}
                  </div>
                )
              ))}

              {/* Custom therapy type content - each has its own tab */}
              {customTherapies.map(ct => (
                activeTab === ct.therapy_type && (
                  <div key={ct.therapy_type}>
                    {renderFeeInput(ct.therapy_type, ct.therapy_type, false)}
                  </div>
                )
              ))}

              {/* "Add New" tab content - only for creating new therapy types */}
              {activeTab === 'others' && (
                <div className="p-6 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg border-2 border-dashed border-gray-300 dark:border-dark-border">
                  <div className="max-w-xl mx-auto text-center mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">
                      Create Custom Therapy Type
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                      Add specialized therapy types like Group Therapy, Art Therapy, Play Therapy, Music Therapy, etc.
                      Each type will get its own tab where you can set the session fee.
                    </p>
                  </div>

                  <div className="max-w-md mx-auto">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                      Therapy Type Name
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCustomType}
                        onChange={(e) => setNewCustomType(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustomTherapy();
                          }
                        }}
                        placeholder="e.g., Group Therapy"
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                      />
                      <button
                        type="button"
                        onClick={addCustomTherapy}
                        className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Create Tab</span>
                      </button>
                    </div>
                  </div>

                  {customTherapies.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-300 dark:border-dark-border">
                      <p className="text-sm text-gray-600 dark:text-dark-text-secondary text-center">
                        <strong>{customTherapies.length}</strong> custom therapy {customTherapies.length === 1 ? 'type' : 'types'} created: {customTherapies.map(ct => ct.therapy_type).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons - Don't show on "Add New" tab */}
            {activeTab !== 'others' && (
              <>
                {!isEditMode && hasSubmittedFees ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditClick();
                    }}
                    className="w-full flex items-center justify-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={saving}
                    className={`w-full flex items-center justify-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors ${
                      saving ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Submit'}
                  </button>
                )}
              </>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default BookingFeeCard;
